/**
 * Payment-Link Link-Tester — Salt-Klassifizierung (Spec 503, Phase 3).
 *
 * Der Salt geht ALS TEXT in HMAC (längen-agnostisch, Handover §4) — der offizielle
 * Demo-Salt `4a6f686e` ist nur 8-Hex. Deshalb blockiert **nur** der leere Salt das
 * Rechnen (WebCrypto lehnt einen leeren HMAC-Key ohnehin ab); Länge ≠ 64 und
 * Nicht-Hex sind **Warnungen**, keine Blocker — sonst würde der offizielle
 * Testvektor abgelehnt (Spec §Edge-Cases, Codex-Fund).
 *
 * Bewusst OHNE Trim: ein Trailing-Newline im Salt ist der klassische 401 und soll
 * als `notHex`-Warnung sichtbar werden, nicht stillschweigend entfernt.
 */

/** Erwartete Länge eines echten Merchant-Salts in Hex-Zeichen. */
export const EXPECTED_SALT_HEX_LENGTH = 64;

export type SaltClassification =
  | { ok: true }
  | { ok: false; level: 'block'; reason: 'empty' }
  | { ok: false; level: 'warn'; reason: 'notHex' | 'wrongLength' };

/**
 * Klassifiziert den eingegebenen Salt:
 * - leer → `block`/`empty` (kein Rechnen),
 * - Nicht-Hex-Zeichen (z. B. Trailing-Newline, Leerzeichen) → `warn`/`notHex`,
 * - gültiges Hex, aber Länge ≠ 64 → `warn`/`wrongLength` (der Demo-Salt läuft),
 * - 64-stelliges Hex → `ok`.
 */
export function classifySalt(input: string): SaltClassification {
  if (input.length === 0) {
    return { ok: false, level: 'block', reason: 'empty' };
  }
  if (!/^[0-9a-fA-F]+$/.test(input)) {
    return { ok: false, level: 'warn', reason: 'notHex' };
  }
  if (input.length !== EXPECTED_SALT_HEX_LENGTH) {
    return { ok: false, level: 'warn', reason: 'wrongLength' };
  }
  return { ok: true };
}

/** Darf mit diesem Salt gerechnet werden? (Nur der leere Salt blockiert.) */
export function canComputeWithSalt(input: string): boolean {
  return input.length > 0;
}
